/**
 * eslint-disable max-depth
 *
 * @format
 */

import React, { useEffect } from 'react';
import { EventCategoryName } from 'core-lib/types/Events';
import { Address, AddressKeys, AddressStateType, AddressStatusType } from 'core-lib/types/Address';
import { useDispatch, useSelector } from 'react-redux';
import { trackEvent } from 'core-lib/utils/events';
import { fetchAddressFromLatLng } from 'core-lib/services/fetchAddressFromLatLng';
import { isParkPlusWebView } from 'core-lib/utils/source';
import { getGeoLocation, LOCATION_STATUS_TYPES } from 'core-lib/utils/location';
import { passMessageToNative } from 'core-lib/utils/nativeBridge';
import { REVERSE_GEO_CODING } from 'core-lib/constants/nativeBridge';
import { useFetchNativeLocation } from 'core-lib/hooks/address/useFetchNativeLocation';
import { UserGeoAddressType, UserLocation, UserLocationData } from 'core-lib/types/Location';
import { updateLocationRequest, updateLocationResponse } from 'core-lib/reduxStore/layout/action';
import { getReverseGeoCodeData, isLocationV2Available } from 'core-lib/utils/locationV2';

function serializeNativeGeoCodeData(data: any): Address {
  return {
    pincode: +data.pincode,
    locality: data.address,
    latitude: data?.lat,
    longitude: data?.lng,
    city: data.city,
    state: data.state,
    country: data.country ?? 'India',
  };
}

export type ReverseGeoCodeResultTypes = {
  address: AddressStateType;
  fetch: (givenLocation?: { lat: number; lng: number }) => void;
};

export type ReverseGeoCodeType = {
  enableRGCApi?: boolean;
  enableFallbackRGCApi?: boolean;
  addressValidation?: AddressKeys[];
  locationV2RequestPayload?: UserLocation;
  geoLocationFlow?: string;
};

declare global {
  interface Window {
    isNativeGeoCodeDone: boolean;
  }
}

/*
   enableFallbackRGCApi:
   This setting allows us to fetch missing or incomplete address details
   from our infra API if the address validation fails with the native data.
   For instance, if the native address lacks necessary fields like city,
   enabling this option ensures we retrieve the complete address information
   from our infra API to meet validation requirements.
*/

/**
 * To get address from native or address
 * @param addressValidation array of string, containing required fields of address that function should return
 * enableRGCApi => used for handling browser case, for getting address from infra api.
 * @returns { fetch, address }: address - object containing address fields, fetch - to start fetching address
 * */

export default function useReverseGeoCode({
  enableRGCApi = true,
  enableFallbackRGCApi = false,
  addressValidation = ['pincode'],
  locationV2RequestPayload = null,
  geoLocationFlow = null,
}: ReverseGeoCodeType): ReverseGeoCodeResultTypes {
  const nativeRgcRef = React.useRef(false);

  let geoCodeTimeout: NodeJS.Timeout;

  const dispatch = useDispatch();

  const [address, setAddress] = React.useState<AddressStateType>({
    status: AddressStatusType.Default,
    data: null,
  });

  const locationV2Result: UserLocationData = useSelector(
    (state: any) => state.layout?.userLocationResponse?.data,
  );

  useEffect(
    () => () => {
      if (geoCodeTimeout) clearTimeout(geoCodeTimeout);
    },
    [],
  );

  const { fetchLocation: fetchLatLngFromNative, loc: nativeLatLng } = useFetchNativeLocation(
    false,
    locationV2RequestPayload,
  );

  async function geoCodingThroughApi(lat, lng, useGeoLocationFlow = false) {
    setAddress({
      status: AddressStatusType.Pending,
      data: null,
    });

    const { locationData, error } = await fetchAddressFromLatLng({
      lat,
      lng,
      ...(useGeoLocationFlow && geoLocationFlow ? { flow: geoLocationFlow } : {}),
    });
    if (locationData) {
      const addressData: Address = {
        pincode: Number(locationData.pincode),
        locality: locationData.locality,
        latitude: +lat,
        longitude: +lng,
        city: locationData.city,
        state: locationData.state ?? '',
        country: 'India',
        infraCity: locationData?.infraCity ?? '',
        addressLine2: locationData?.addressLine2 ?? '',
      };
      const isValid = addressValidation.every(el => addressData[el]);
      if (isValid) {
        setAddress({
          status: AddressStatusType.Success,
          data: addressData,
        });
      } else {
        setAddress({
          status: AddressStatusType.Failed,
          data: null,
        });
      }
    }
    if (error) {
      setAddress({
        status: AddressStatusType.Failed,
        data: null,
      });
    }
  }

  function geoCodingThroughNative(lat, lng) {
    trackEvent({
      name: 'native_reverse_geo_start',
      defaultAttr: {},
      attributes: { category: EventCategoryName.Address },
    });
    nativeRgcRef.current = true;
    window.isNativeGeoCodeDone = false;

    geoCodeTimeout = setTimeout(() => {
      if (window.isNativeGeoCodeDone === false) {
        geoCodingThroughApi(lat, lng);
      }
    }, 7000);

    passMessageToNative(REVERSE_GEO_CODING, null, {
      lat,
      lng,
      callback_script: `
        try{
        window.isNativeGeoCodeDone = true;
        console.info('callback_script fired',window,document);
        window.sessionStorage.setItem('geoCodingDataW1', JSON.stringify({{geoCodingData}}));
        var geoCodeData = window.sessionStorage.getItem('geoCodingDataW1');
        var parsedGeoCodeData = JSON.parse(geoCodeData);
        parsedGeoCodeData.lat = ${lat};
        parsedGeoCodeData.lng = ${lng};
        window.sessionStorage.setItem('geoCodingDataW1', JSON.stringify(parsedGeoCodeData));
        window.dispatchEvent(new Event('storageWrong'));
        console.info("From Native : Wrong Script",window.sessionStorage.getItem('geoCodingDataW1'));
        }catch(e){
          console.error('callback_script error',e);
        }
        `,
    });
    passMessageToNative(REVERSE_GEO_CODING, null, {
      lat,
      lng,
      callback_script: `
        try{
        window.isNativeGeoCodeDone = true;
        console.info('callback_script fired',window,document);
        window.sessionStorage.setItem('geoCodingData', JSON.stringify({{geoCodingData}}));
        var geoCodeData = window.sessionStorage.getItem('geoCodingData');
        var parsedGeoCodeData = JSON.parse(geoCodeData);
        parsedGeoCodeData.lat = ${lat};
        parsedGeoCodeData.lng = ${lng};
        window.sessionStorage.setItem('geoCodingData', JSON.stringify(parsedGeoCodeData));
        window.dispatchEvent(new Event('storage'));
        console.info("From Native :",window.sessionStorage.getItem('geoCodingData'));
        }catch(e){
          console.error('callback_script error',e);
        }
        `,
    });
  }

  async function fetchDeviceLoc() {
    const locObj: any = {};
    try {
      const geoLoc = await getGeoLocation();
      locObj.status = LOCATION_STATUS_TYPES.Success;
      locObj.coords = geoLoc;
    } catch (err) {
      locObj.status = err;
    }

    return locObj;
  }

  // To fetch coordinates from browser and proceed further
  async function fetchLocationFromBrowser(useGeoLocationFlow = false) {
    const result = await fetchDeviceLoc();
    if (result?.status === LOCATION_STATUS_TYPES.Success) {
      const { lat, lng } = result.coords;
      if (enableRGCApi) geoCodingThroughApi(lat, lng, useGeoLocationFlow);
    } else if (result?.status === LOCATION_STATUS_TYPES.Denied) {
      setAddress({
        status: AddressStatusType.Denied,
        data: null,
      });
    }
  }

  // This will execute once native sends callback
  function storageChangeCallback() {
    if (nativeRgcRef.current) {
      nativeRgcRef.current = false;
      const localGeoData = sessionStorage.getItem('geoCodingData');
      if (localGeoData) {
        const data = JSON.parse(localGeoData);
        if (data) {
          trackEvent({
            name: 'native_reverse_geo_success',
            defaultAttr: {},
            attributes: { category: EventCategoryName.Address },
          });
          if (data && data.address) {
            const serializedData: Address = serializeNativeGeoCodeData(data);
            const isValid = addressValidation.every(
              el => serializedData[el] && serializedData[el] !== '',
            );
            if (isValid) {
              setAddress({
                status: AddressStatusType.Success,
                data: serializedData,
              });
            } else if (enableFallbackRGCApi) {
              geoCodingThroughApi(data.lat, data.lng);
            }
          } else {
            geoCodingThroughApi(data.lat, data.lng);
          }
        }
      }
    }
  }

  useEffect(() => {
    if (locationV2Result) {
      const serializedData: Address =
        locationV2Result.data ??
        (locationV2Result.coords
          ? {
              latitude: locationV2Result.coords.lat,
              longitude: locationV2Result.coords.lon,
              locality: '',
            }
          : null);
      if (serializedData && locationV2Result.status === AddressStatusType.Success) {
        const isValid = addressValidation.every(
          el => serializedData[el] && serializedData[el] !== '',
        );
        if (isValid) {
          setAddress({
            status: AddressStatusType.Success,
            data: serializedData,
          });
        } else if (enableFallbackRGCApi) {
          geoCodingThroughApi(serializedData.latitude, serializedData.longitude);
        }
      } else {
        setAddress({
          status: locationV2Result.status,
          data: locationV2Result.data,
        });
      }

      dispatch(
        updateLocationResponse({
          data: null,
        }),
      );
    }
  }, [locationV2Result]);

  async function getDataFromLatAndLng(lat: number, lon) {
    const loc: UserLocationData = {
      status: AddressStatusType.Default,
      coords: {
        lat,
        lon,
      },
      data: null,
    };

    const geoAddressTypeVal = UserGeoAddressType.LatLng;

    // fetch the geo address based on the configuration
    const { response: geoCodeResponse, error: geoCodeError } = await getReverseGeoCodeData({
      locationType: geoAddressTypeVal,
      latLng: loc.coords,
    });

    if (geoCodeResponse) {
      loc.status = AddressStatusType.Success;
      loc.data = geoCodeResponse;
    } else {
      loc.status = AddressStatusType.Failed;
    }

    dispatch(
      updateLocationResponse({
        data: loc,
      }),
    );
  }

  function fetch(givenLocation?: { lat: number; lng: number }) {
    let lat: string = null;
    let lng: string = null;
    setAddress({
      status: AddressStatusType.Default,
      data: null,
    });

    if (givenLocation && givenLocation.lat && givenLocation.lng) {
      lat = givenLocation.lat.toString();
      lng = givenLocation.lng.toString();
    }

    const useGeoLocationFlow = !!geoLocationFlow;

    if (isLocationV2Available() && !!locationV2RequestPayload) {
      if (lat && lng) {
        getDataFromLatAndLng(+lat, +lng);
      } else {
        dispatch(updateLocationRequest(locationV2RequestPayload));
      }
      return;
    }

    if (isParkPlusWebView()) {
      if (lat && lng) {
        geoCodingThroughNative(lat, lng);
      } else {
        fetchLatLngFromNative();
      }
    } else if (enableRGCApi) {
      if (lat && lng) {
        geoCodingThroughApi(lat, lng, useGeoLocationFlow);
      } else {
        fetchLocationFromBrowser(useGeoLocationFlow);
      }
    } else {
      setAddress({
        status: AddressStatusType.Failed,
        data: null,
      });
    }
  }

  useEffect(() => {
    if (nativeLatLng && nativeLatLng.status === AddressStatusType.Success) {
      if (nativeLatLng.coords && nativeLatLng.coords.lat && nativeLatLng.coords.lng) {
        fetch({ lat: +nativeLatLng.coords.lat, lng: +nativeLatLng.coords.lng });
      }
    }
    if (nativeLatLng && nativeLatLng.status === AddressStatusType.Denied) {
      setAddress({
        status: AddressStatusType.Denied,
        data: null,
      });
    }
    if (nativeLatLng && nativeLatLng.status === AddressStatusType.Failed) {
      setAddress({
        status: AddressStatusType.Failed,
        data: null,
      });
    }
  }, [nativeLatLng]);

  useEffect(() => {
    window.addEventListener('storage', () => {
      storageChangeCallback();
    });
    return () =>
      window.removeEventListener('storage', () => {
        storageChangeCallback();
      });
  }, []);

  return {
    address,
    fetch,
  };
}
